/**
 * useLocationFilter Hook
 * 
 * Shared hook for location filtering across all pages.
 * Provides:
 * - Country/city data from API
 * - Selected country/city state management
 * - Cities filtered by selected country
 */

import { useState, useEffect, useMemo, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Cache for countries data (shared across hook instances)
let countriesCache = null;
let countriesFetchPromise = null;

/**
 * Fetch countries from API (with caching)
 */
const fetchCountries = async () => {
  if (countriesCache) return countriesCache;
  
  if (countriesFetchPromise) return countriesFetchPromise;
  
  countriesFetchPromise = fetch(`${API_BASE}/api/student-hub/countries`)
    .then(r => r.ok ? r.json() : [])
    .then(data => {
      countriesCache = data;
      return data;
    })
    .catch(() => {
      countriesFetchPromise = null;
      return [];
    });
  
  return countriesFetchPromise;
};

/**
 * Clear countries cache (useful for testing)
 */
export const clearCountriesCache = () => {
  countriesCache = null;
  countriesFetchPromise = null;
};

/**
 * useLocationFilter Hook
 * 
 * @param {Object} options
 * @param {string} options.initialCountry - Initial country key
 * @param {string} options.initialCity - Initial city key
 * @returns {Object} Location filter state and methods
 */
export default function useLocationFilter({ initialCountry = '', initialCity = '' } = {}) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [selectedCity, setSelectedCity] = useState(initialCity);

  // Fetch countries on mount
  useEffect(() => {
    setLoading(true);
    fetchCountries()
      .then(setCountries)
      .finally(() => setLoading(false));
  }, []);

  // Get cities for selected country
  const citiesForCountry = useMemo(() => {
    if (!selectedCountry) return [];
    const country = countries.find(c => c.key === selectedCountry);
    return country?.cities || [];
  }, [selectedCountry, countries]);

  // Handle country change (clears city)
  const handleCountryChange = useCallback((countryKey) => {
    setSelectedCountry(countryKey);
    setSelectedCity(''); // Reset city when country changes
  }, []);

  // Handle city change
  const handleCityChange = useCallback((cityKey) => {
    setSelectedCity(cityKey);
  }, []);

  // Clear location filters
  const clearLocation = useCallback(() => {
    setSelectedCountry('');
    setSelectedCity('');
  }, []);

  // Get display labels for selected location
  const getCountryLabel = useCallback((isRTL = false) => {
    const country = countries.find(c => c.key === selectedCountry);
    if (!country) return '';
    return isRTL ? country.name_ar : country.name_en;
  }, [countries, selectedCountry]);

  const getCityLabel = useCallback((isRTL = false) => {
    const city = citiesForCountry.find(c => c.key === selectedCity);
    if (!city) return '';
    return isRTL ? city.name_ar : city.name_en;
  }, [citiesForCountry, selectedCity]);

  // Check if location filter is active
  const hasLocationFilter = selectedCountry || selectedCity;

  return {
    // Data
    countries,
    citiesForCountry,
    loading,
    
    // Selected values
    selectedCountry,
    selectedCity,
    
    // Setters
    setSelectedCountry: handleCountryChange,
    setSelectedCity: handleCityChange,
    
    // Helpers
    clearLocation,
    getCountryLabel,
    getCityLabel,
    hasLocationFilter,
  };
}

export { fetchCountries };
